"use client";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import CustomCheckbox from "./custom-checkbox";
import {
  ClientConsentItem,
  LeadFormCmsItem,
  LeadFormValues,
  LeadFormErrors,
  LeadFormFieldKey,
  LeadFormPageType,
  LeadFormSubmissionContext,
  AUTH_FIELD_KEYS,
  EMAIL_REGEX,
} from "./types";
import { getLeadFormFieldKey, hasField } from "./field-registry";
import { useLeadFormSubmit } from "./hooks/useLeadFormSubmit";
import { containsProfanity, getHighlightedText, PROFANITY_ERROR_MESSAGE } from "./utils/profanity-filter";
import { getCommonAPIHeaders,isPGS } from "@packages/lib/utlils/commonFunction";
import API_URL from "@packages/lib/server-actions/api-urls";
import LeadFormFieldsSkeleton from "@packages/shared-components/skeleton/leadform-fields-skeleton";
import { v4 as uuidv4 } from "uuid";
import { GAConstants } from "@packages/shared-components/services/constants";

interface LeadFormFeildsProps {
  pageType?: LeadFormPageType;
  cmsItems: LeadFormCmsItem[];
  submissionContext: LeadFormSubmissionContext;
  submitLabel?: string;
  onSubmitSuccess?: (formValues?: { yearOfEntry?: string; marketingConsent?: string }) => void;
  studyLevelOptions?: { id: number; value: string; label: string }[];
  openDayOptions?: string[];
  yearOptions?: string[];
  enquiryPlaceholder?: string;
  currentStep?: number;
  totalSteps?: number;
  allCmsItems?: LeadFormCmsItem[];
  isAuthenticated?: boolean;
  clientConsents?: ClientConsentItem[];
  userDetails?: any;
}

// --- Constants (matching signupform.tsx) ---
const NAME_MAX_LENGTH = 40;
const PASSWORD_MAX_LENGTH = 128;
const ENQUIRY_MAX_LENGTH = 4000;
const EMAIL_MAX_LENGTH = 100;
const EMAIL_LIMIT_REACHED_MESSAGE = `Maximum ${EMAIL_MAX_LENGTH} characters reached`;
const INVALID_CHAR_ERROR_MESSAGE = "Invalid character";
const NAME_LIMIT_REACHED_MESSAGE = `Maximum ${NAME_MAX_LENGTH} characters reached`;
const PASSWORD_LIMIT_REACHED_MESSAGE = `Maximum ${PASSWORD_MAX_LENGTH} characters reached`;
const TRANSIENT_MESSAGES = new Set([
  INVALID_CHAR_ERROR_MESSAGE,
  NAME_LIMIT_REACHED_MESSAGE,
  PASSWORD_LIMIT_REACHED_MESSAGE,
]);
const INVALID_CHAR_VISIBLE_MS = 500;
const INVALID_CHAR_ERROR_TIMEOUT_MS = INVALID_CHAR_VISIBLE_MS + 1200;
const INPUT_CONTROL_KEYS = [
  "Backspace", "Delete", "Tab", "Escape", "Enter",
  "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End",
];

const sanitizeNameValue = (value: string) =>
  value.replace(/[^A-Za-z]/g, "").slice(0, NAME_MAX_LENGTH);

const allowedEmailKeyPattern = /^[A-Za-z0-9@._%+-]$/;
const sanitizeEmailValue = (value: string) => value.replace(/\s+/g, "").replace(/[^A-Za-z0-9@._%+-]/g, "");

interface PasswordRuleState {
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
  hasMinLength: boolean;
}

const getPasswordRuleState = (password: string): PasswordRuleState => ({
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /\d/.test(password),
  hasSpecial: /[^A-Za-z0-9]/.test(password),
  hasMinLength: password.length >= 8,
});

const INITIAL_VALUES: LeadFormValues = {
  enquiry: "",
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  yearOfEntry: "",
  studyLevel: "",
  postcode: "",
  openDayEventId: "",
  stayInKnow: false,
  uniNewsletter: false,
  countryOfResidence: "United Kingdom",
  addressLine1: "",
  addressLine2: "",
  townCity: "",
  addressPostcode: "",
  countrySelector: "",
};

const API_DOMAIN = process.env.NEXT_PUBLIC_BFF_API_DOMAIN || "";

interface SectionOption {
  id: number;
  value: string;
  label: string;
}

const LeadFormFeilds = ({
  pageType = "requestinfo",
  cmsItems,
  submissionContext,
  submitLabel,
  onSubmitSuccess,
  yearOptions = ["2026", "2027", "2028", "2029"],
  enquiryPlaceholder,
  currentStep = 0,
  totalSteps = 1,
  allCmsItems,
  isAuthenticated = false,
  clientConsents,
  userDetails,
}: LeadFormFeildsProps) => {
    console.log(cmsItems,"cmsItems")
  const domain = `${process.env.NEXT_PUBLIC_ENVIRONMENT === "dev" ? "https://mdev.dev.aws." : process.env.NEXT_PUBLIC_ENVIRONMENT === "stg" ? "https://mtest.test.aws." : "https://www."}`;

  // Build dynamic enquiry pre-fill based on course/university context
  const defaultEnquiry = (() => {
    const uniName = submissionContext?.collegeName || submissionContext?.institutionNameDisplay || "";
    const courseName = submissionContext?.courseName || "";
    const siteName = isPGS() ? "postgraduatesearch.com" : "Whatuni.com";
    if (courseName && uniName) {
      return `I read about the ${courseName} offered by ${uniName} on ${siteName} and would like to request more information about the course.`;
    }
    if (uniName) {
      return `I read about ${uniName} on ${siteName} and would like to request more information about the institution.`;
    }
    return "";
  })();

  const [values, setValues] = useState<LeadFormValues>({ ...INITIAL_VALUES, enquiry: defaultEnquiry });
  const [errors, setErrors] = useState<LeadFormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [checkedConsents, setCheckedConsents] = useState<Set<string>>(new Set());
  const [openDayDropdownOpen, setOpenDayDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);

  // Address flow states
  const [addressFound, setAddressFound] = useState(false);
  const [showManualFields, setShowManualFields] = useState(false);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState<{ placeName: string; addressLine1: string; addressLine2: string; city: string; postcode: string }[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);

  // Dynamic options fetched from sections API
  const [studyLevelOptions, setStudyLevelOptions] = useState<SectionOption[]>([]);
  const [countryList, setCountryList] = useState<{ id: number; value: string; label: string; ukFlag: string }[]>([]);
  const [yearOfEntryOptions, setYearOfEntryOptions] = useState<SectionOption[]>([]);

  // Transient error animation (from signupform.tsx)
  type TransientField = "firstName" | "lastName" | "password";
  const [isInvalidCharFading, setIsInvalidCharFading] = useState<Record<TransientField, boolean>>({
    firstName: false, lastName: false, password: false,
  });
  const invalidCharFadeTimerRef = useRef<Partial<Record<TransientField, ReturnType<typeof setTimeout>>>>({});
  const invalidCharClearTimerRef = useRef<Partial<Record<TransientField, ReturnType<typeof setTimeout>>>>({});

  // Email validation state (matching getemailform.tsx pattern)
  const [emailShakeTick, setEmailShakeTick] = useState(0);
  const [isEmailInvalidCharFading, setIsEmailInvalidCharFading] = useState(false);
  const emailInvalidCharFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emailInvalidCharClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Profanity highlight state
  const [hasProfanity, setHasProfanity] = useState(false);
  const enquiryRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  // Derived
  const isUK = values.countryOfResidence === "United Kingdom";
  const isROW = values.countryOfResidence === "Rest of the world";
  const hasCountryField = cmsItems.some((item) => getLeadFormFieldKey(item) === "countryOfResidence");
  const showAddressFields = !hasCountryField || isROW || (isUK && (addressFound || showManualFields));
  const isMultiStep = totalSteps > 1;
  const isLastStep = currentStep >= totalSteps - 1;
  const submitCmsItems = allCmsItems || cmsItems;

  const {
    isSubmitting,
    isRegistering: _isRegistering,
    formError,
    handleSubmit,
    callPartialRegistration,
    checkEmailExists,
    setFormError,
  } = useLeadFormSubmit(pageType, submissionContext, submitCmsItems, userDetails);

  const passwordRuleState = getPasswordRuleState(values.password);
  const hasPasswordInput = values.password.length > 0;
  const getPasswordHelperColorClass = (isMatched: boolean) => {
    if (!hasPasswordInput) return "text-grey300";
    return isMatched ? "text-positive-default" : "text-negative-default";
  };

  // Cleanup transient timers
  useEffect(() => {
    return () => {
      Object.values(invalidCharFadeTimerRef.current).forEach((t) => { if (t) clearTimeout(t); });
      Object.values(invalidCharClearTimerRef.current).forEach((t) => { if (t) clearTimeout(t); });
      if (emailInvalidCharFadeTimerRef.current) clearTimeout(emailInvalidCharFadeTimerRef.current);
      if (emailInvalidCharClearTimerRef.current) clearTimeout(emailInvalidCharClearTimerRef.current);
    };
  }, []);

  // Fetch studyLevel options + prefill user details (from prop) on mount
  useEffect(() => {
    const fetchSections = async () => {
      const sectionsToFetch: string[] = [];
      if (hasField(cmsItems, "studyLevel")) sectionsToFetch.push("STUDY_LEVEL");
      if (hasField(cmsItems, "yearOfEntry")) sectionsToFetch.push("YOE");
      if (cmsItems.some((item) => getLeadFormFieldKey(item) === "addressBlock")) sectionsToFetch.push("COUNTRY_LIST");
      if (sectionsToFetch.length === 0) return null;
      try {
        const response = await fetch(`${API_DOMAIN}${API_URL.leadProfileSections}`, {
          method: "POST",
          headers: {
            ...getCommonAPIHeaders(),
            siteCode: process.env.SITE_CODE || "WU_WEB",
            Lp: "Y",
            "x-correlation-id": uuidv4(),
          },
          body: JSON.stringify({ sections: sectionsToFetch }),
        });
        if (!response.ok) return null;
        const data = await response.json();
        if (data?.sections?.countryList) {
          setCountryList(data.sections.countryList);
        }
        if (data?.sections?.yoe) {
          setYearOfEntryOptions(data.sections.yoe);
        }
        return data?.sections?.studyLevel || [];
      } catch {
        return null;
      }
    };

    const init = async () => {
      // Fetch studyLevel options
      let options = await fetchSections();
      // For PGS, hide first option (Undergraduate) and default to second (Postgraduate)
      if (options && isPGS() && options.length > 1) {
        options = options.slice(1);
      }
      if (options) {
        setStudyLevelOptions(options);
      }

      // Prefill form from userDetails prop (passed from page-level enquiry call)
      // Priority: userDetails values first, then default to 0-index if not available
      if (userDetails) {
        setValues((prev) => ({
          ...prev,
          ...(userDetails.foreName && !prev.firstName ? { firstName: userDetails.foreName } : {}),
          ...(userDetails.surname && !prev.lastName ? { lastName: userDetails.surname } : {}),
          ...(userDetails.email && !prev.email ? { email: userDetails.email } : {}),
          ...(userDetails.postcode && !prev.postcode ? { postcode: userDetails.postcode } : {}),
          ...(userDetails.address1 && !prev.addressLine1 ? { addressLine1: userDetails.address1 } : {}),
          ...(userDetails.address2 && !prev.addressLine2 ? { addressLine2: userDetails.address2 } : {}),
          ...(userDetails.town && !prev.townCity ? { townCity: userDetails.town } : {}),
          ...(userDetails.postcode && !prev.addressPostcode ? { addressPostcode: userDetails.postcode } : {}),
          ...(userDetails.country ? { countryOfResidence: userDetails.country === "United Kingdom" ? "United Kingdom" : "Rest of the world" } : {}),
          // yearOfEntry: use userDetails value if available, otherwise default to first option from API
          yearOfEntry: userDetails.yearOfEntry ? String(userDetails.yearOfEntry) : (prev.yearOfEntry || ""),
          // studyLevel: use userDetails value if available, otherwise default to first option from API
          studyLevel: userDetails.studyLevel ? String(userDetails.studyLevel) : (prev.studyLevel || (options?.[0] ? String(options[0].id) : "")),
        }));
      } else {
        // No userDetails — default to first options from API
        setValues((prev) => ({
          ...prev,
          yearOfEntry: prev.yearOfEntry || "",
          studyLevel: prev.studyLevel || (options?.[0] ? String(options[0].id) : ""),
        }));
      }
    };

    init();
  }, [cmsItems, userDetails]);

  // --- Transient error helpers (from signupform.tsx) ---
  const clearInvalidCharAnimation = (field: TransientField) => {
    const fadeTimer = invalidCharFadeTimerRef.current[field];
    if (fadeTimer) clearTimeout(fadeTimer);
    const clearTimer = invalidCharClearTimerRef.current[field];
    if (clearTimer) clearTimeout(clearTimer);
    setIsInvalidCharFading((prev) => ({ ...prev, [field]: false }));
  };

  const setTransientError = (field: TransientField, message: string = INVALID_CHAR_ERROR_MESSAGE) => {
    clearInvalidCharAnimation(field);
    setErrors((prev) => ({ ...prev, [field]: message }));
    invalidCharFadeTimerRef.current[field] = setTimeout(() => {
      setIsInvalidCharFading((prev) => ({ ...prev, [field]: true }));
    }, INVALID_CHAR_VISIBLE_MS);
    invalidCharClearTimerRef.current[field] = setTimeout(() => {
      setErrors((prev) =>
        TRANSIENT_MESSAGES.has(prev[field] || "") ? { ...prev, [field]: undefined } : prev,
      );
      setIsInvalidCharFading((prev) => ({ ...prev, [field]: false }));
    }, INVALID_CHAR_ERROR_TIMEOUT_MS + 80);
  };

  const onChange = (field: keyof LeadFormValues, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const clearError = (field: LeadFormFieldKey) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  /** Email validation helpers (matching getemailform.tsx pattern) */
  const clearEmailInvalidCharTimers = () => {
    if (emailInvalidCharFadeTimerRef.current) clearTimeout(emailInvalidCharFadeTimerRef.current);
    if (emailInvalidCharClearTimerRef.current) clearTimeout(emailInvalidCharClearTimerRef.current);
    emailInvalidCharFadeTimerRef.current = null;
    emailInvalidCharClearTimerRef.current = null;
  };

  const triggerEmailError = (message: string) => {
    setErrors((prev) => ({ ...prev, email: message }));
    setEmailShakeTick((prev) => prev + 1);
  };

  const setTransientEmailError = (message: string) => {
    clearEmailInvalidCharTimers();
    setIsEmailInvalidCharFading(false);
    triggerEmailError(message);
    emailInvalidCharFadeTimerRef.current = setTimeout(() => {
      setIsEmailInvalidCharFading(true);
    }, INVALID_CHAR_VISIBLE_MS);
    emailInvalidCharClearTimerRef.current = setTimeout(() => {
      setErrors((prev) => (prev.email === message ? { ...prev, email: undefined } : prev));
      setIsEmailInvalidCharFading(false);
    }, INVALID_CHAR_ERROR_TIMEOUT_MS + 80);
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    if (e.ctrlKey || e.metaKey || INPUT_CONTROL_KEYS.includes(key)) return;

    if (key === " " || !allowedEmailKeyPattern.test(key)) {
      e.preventDefault();
      setTransientEmailError(INVALID_CHAR_ERROR_MESSAGE);
      return;
    }

    if (values.email.length >= EMAIL_MAX_LENGTH) {
      e.preventDefault();
      triggerEmailError(EMAIL_LIMIT_REACHED_MESSAGE);
    }
  };

  const handleEmailPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedValue = e.clipboardData.getData("text");
    const sanitized = sanitizeEmailValue(pastedValue);
    const truncated = sanitized.slice(0, EMAIL_MAX_LENGTH);
    if (truncated.length >= EMAIL_MAX_LENGTH && sanitized.length > EMAIL_MAX_LENGTH) {
      triggerEmailError(EMAIL_LIMIT_REACHED_MESSAGE);
    } else if (sanitized !== pastedValue) {
      setTransientEmailError(INVALID_CHAR_ERROR_MESSAGE);
    } else {
      clearEmailInvalidCharTimers();
      setIsEmailInvalidCharFading(false);
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
    onChange("email", truncated);
  };

  /** Email blur handler — check exists if valid email (skip for prospectus forms — DLG-10182) */
  const handleEmailBlur = async () => {
    if (pageType === "digitalprospectus" || pageType === "physicalprospectus") return;
    const email = values.email.trim();
    if (email && EMAIL_REGEX.test(email)) {
      const result = await checkEmailExists(email);
      if (result.exists) {
        setFormError("Email address already exists, Please Sign in");
      }
    }
  };

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isMultiStep && !isLastStep) {
      // Multi-step intermediate step: validate only current step's visible fields
      const stepErrors: LeadFormErrors = {};
      if (!isAuthenticated) {
        if (hasField(cmsItems, "firstName") && !values.firstName.trim())
          stepErrors.firstName = "We still don't know your name. Remind us?";
        if (hasField(cmsItems, "lastName") && !values.lastName.trim())
          stepErrors.lastName = "We still don't know your name. Remind us?";
        if (hasField(cmsItems, "email")) {
          if (!values.email.trim())
            stepErrors.email = "Please enter a valid email address";
          else if (!EMAIL_REGEX.test(values.email.trim()))
            stepErrors.email = "Please enter a valid email address";
        }
        if (hasField(cmsItems, "password") && !values.password)
          stepErrors.password = "We still don't know your password. Remind us?";
      }
      if (hasField(cmsItems, "studyLevel") && !values.studyLevel)
        stepErrors.studyLevel = "Please select your study level";
      if (hasField(cmsItems, "enquiry")) {
        const enquiryText = values.enquiry.trim();
        if (!enquiryText) stepErrors.enquiry = "Please enter your enquiry";
        else if (containsProfanity(enquiryText)) stepErrors.enquiry = PROFANITY_ERROR_MESSAGE;
      }

      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        return;
      }

      // Physical prospectus step 1: fire registration API in background (fire-and-forget)
      if (pageType === "physicalprospectus" && !isAuthenticated) {
        callPartialRegistration(values);
      }

      if (onSubmitSuccess) onSubmitSuccess({
        yearOfEntry: values.yearOfEntry || undefined,
        marketingConsent: (values.stayInKnow || checkedConsents.size > 0) ? GAConstants.marketing_consent_value : GAConstants.notApplicable,
      });
      return;
    }

    // Last step or single step: block if profanity still present
    if (hasField(submitCmsItems, "enquiry") && containsProfanity(values.enquiry.trim())) {
      setErrors((prev) => ({ ...prev, enquiry: PROFANITY_ERROR_MESSAGE }));
      setHasProfanity(true);
      return;
    }

    // Last step or single step: full API submit
    const submitResult = await handleSubmit(values, isAuthenticated, checkedConsents);
    if (submitResult.errors && Object.keys(submitResult.errors).length > 0) {
      setErrors(submitResult.errors);
    } else if (submitResult.success && onSubmitSuccess) {
      onSubmitSuccess({
        yearOfEntry: values.yearOfEntry || undefined,
        marketingConsent: (values.stayInKnow || checkedConsents.size > 0) ? GAConstants.marketing_consent_value : GAConstants.notApplicable,
      });
    }
  };

  /** Check if a field should be hidden (auth fields hidden when signed in) */
  const isFieldHidden = (fieldKey: LeadFormFieldKey): boolean => {
    if (isAuthenticated && AUTH_FIELD_KEYS.includes(fieldKey)) return true;
    // Hide study level on PGS prospectus forms — default value is still submitted
    if (fieldKey === "studyLevel" && isPGS() && (pageType === "digitalprospectus" || pageType === "physicalprospectus")) return true;
    return false;
  };

  /** Renders a name input with signupform-style validation (keyDown, paste, transient errors) */
  const renderNameField = (fieldKey: "firstName" | "lastName", label: string) => (
    <div className="flex-1 flex flex-col gap-[4px]" key={fieldKey}>
      <label htmlFor={fieldKey} className="text-small font-semibold">
        {label}<span className="text-negative-default">*</span>
      </label>
      <input
        type="text"
        className={`input-textbox ${errors[fieldKey] ? "border-negative-default" : ""}`}
        id={fieldKey}
        maxLength={NAME_MAX_LENGTH}
        value={values[fieldKey]}
        onKeyDown={(e) => {
          const key = e.key;
          if (e.ctrlKey || e.metaKey || INPUT_CONTROL_KEYS.includes(key)) return;
          if (!/^[A-Za-z]$/.test(key)) { e.preventDefault(); setTransientError(fieldKey); return; }
          if (values[fieldKey].length >= NAME_MAX_LENGTH) { e.preventDefault(); setTransientError(fieldKey, NAME_LIMIT_REACHED_MESSAGE); }
        }}
        onPaste={(e) => {
          e.preventDefault();
          const pasted = e.clipboardData.getData("text");
          const sanitized = sanitizeNameValue(pasted);
          if (sanitized.length >= NAME_MAX_LENGTH && pasted.length > NAME_MAX_LENGTH) setTransientError(fieldKey, NAME_LIMIT_REACHED_MESSAGE);
          else if (sanitized !== pasted) setTransientError(fieldKey);
          else clearError(fieldKey);
          onChange(fieldKey, sanitized);
        }}
        onChange={(e) => {
          const sanitized = sanitizeNameValue(e.target.value);
          if (sanitized.length >= NAME_MAX_LENGTH && e.target.value.length > sanitized.length) setTransientError(fieldKey, NAME_LIMIT_REACHED_MESSAGE);
          else if (sanitized !== e.target.value) setTransientError(fieldKey);
          else { clearInvalidCharAnimation(fieldKey); clearError(fieldKey); }
          onChange(fieldKey, sanitized);
        }}
      />
      <p role="alert" className={`x-small min-h-[18px] ${
        errors[fieldKey]
          ? `text-negative-default ${TRANSIENT_MESSAGES.has(errors[fieldKey] || "") ? (isInvalidCharFading[fieldKey] ? "transition-opacity duration-[1200ms] ease-out opacity-0" : "opacity-100") : "opacity-100"}`
          : "hidden"
      }`}>{errors[fieldKey] || "\u00A0"}</p>
    </div>
  );

  /** Static address fields block */
  const renderAddressFields = () => (
    <>
      <div className="flex flex-col gap-[4px]">
        <label className="small font-semibold">Address line 1<span className="text-negative-default">*</span></label>
        <input type="text" className={`input-textbox w-full ${errors.addressBlock && !values.addressLine1.trim() ? "border-negative-default" : ""}`}
          value={values.addressLine1}
          onChange={(e) => { onChange("addressLine1", e.target.value); clearError("addressBlock"); }}
        />
      </div>
      <div className="flex flex-col gap-[4px]">
        <label className="small font-semibold">Address line 2<span className="text-negative-default">*</span></label>
        <input type="text" className={`input-textbox w-full ${errors.addressBlock && !values.addressLine2.trim() ? "border-negative-default" : ""}`}
          value={values.addressLine2}
          onChange={(e) => { onChange("addressLine2", e.target.value); clearError("addressBlock"); }}
        />
      </div>
      <div className="flex flex-col md:flex-row gap-[24px] md:gap-[8px]">
        <div className="flex-1 flex flex-col gap-[4px]">
          <label className="small font-semibold">Town/city<span className="text-negative-default">*</span></label>
          <input type="text" className={`input-textbox w-full ${errors.addressBlock && !values.townCity.trim() ? "border-negative-default" : ""}`}
            value={values.townCity}
            onChange={(e) => { onChange("townCity", e.target.value); clearError("addressBlock"); }}
          />
        </div>
        <div className="flex-1 flex flex-col gap-[4px]">
          <label className="small font-semibold">Postcode<span className="text-negative-default">*</span></label>
          <input type="text" className={`input-textbox w-full ${errors.addressBlock && !values.addressPostcode.trim() ? "border-negative-default" : ""}`}
            value={values.addressPostcode}
            onChange={(e) => { onChange("addressPostcode", e.target.value); clearError("addressBlock"); }}
          />
        </div>
      </div>
      {errors.addressBlock && <p className="x-small text-negative-default">{errors.addressBlock}</p>}
    </>
  );

  /** Renders a dynamic field based on CMS item */
  const renderDynamicField = (item: LeadFormCmsItem, index: number) => {
    const fieldKey = getLeadFormFieldKey(item);
    if (isFieldHidden(fieldKey)) return null;

    switch (fieldKey) {
      // --- Enquiry with profanity filter ---
      case "enquiry":
        return (
          <div className="flex flex-col gap-[4px]" key="enquiry">
            <label className="text-small font-semibold">
              {item.cardSectionTitle || "Your enquiry"}
              <span className="text-negative-default">*</span>
            </label>
            <div className="relative">
              <div
                ref={highlightRef}
                className="input-textbox h-full w-full absolute top-0 left-0 pointer-events-none overflow-hidden whitespace-pre-wrap break-words text-transparent border-transparent"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: getHighlightedText(values.enquiry) }}
              />
              <textarea
                ref={enquiryRef}
                rows={3}
                className={`input-textbox resize-y overflow-hidden placeholder-grey500 w-full relative bg-transparent ${errors.enquiry ? "border-negative-default" : ""}`}
                placeholder={enquiryPlaceholder || ""}
                maxLength={ENQUIRY_MAX_LENGTH}
                value={values.enquiry}
                onInput={(e) => {
                  // Auto-expand height to fit content (no scrollbar)
                  const target = e.currentTarget;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                onChange={(e) => {
                  const newValue = e.target.value;
                  onChange("enquiry", newValue);

                  // Live profanity check — show error, highlight words, block submit
                  if (containsProfanity(newValue)) {
                    setHasProfanity(true);
                    setErrors((prev) => ({ ...prev, enquiry: PROFANITY_ERROR_MESSAGE }));
                  } else {
                    setHasProfanity(false);
                    clearError("enquiry");
                  }
                }}
              />
            </div>
            {errors.enquiry && <p className="x-small text-negative-default">{errors.enquiry}</p>}
          </div>
        );

      // --- Name fields (auto-group adjacent firstName + lastName) ---
      case "firstName":
      case "lastName": {
        const nextItem = cmsItems[index + 1];
        const prevItem = cmsItems[index - 1];
        const nextKey = nextItem ? getLeadFormFieldKey(nextItem) : null;
        const prevKey = prevItem ? getLeadFormFieldKey(prevItem) : null;

        if (fieldKey === "firstName" && nextKey === "lastName") {
          if (isFieldHidden("lastName")) return renderNameField(fieldKey, item.cardSectionTitle || "First name");
          return (
            <div className="flex flex-col md:flex-row gap-[24px] md:gap-[8px]" key="name-group">
              {renderNameField("firstName", item.cardSectionTitle || "First name")}
              {renderNameField("lastName", nextItem.cardSectionTitle || "Last name")}
            </div>
          );
        }
        if (fieldKey === "lastName" && prevKey === "firstName" && !isFieldHidden("firstName")) return null;

        return (
          <div className="flex flex-col md:flex-row gap-[24px] md:gap-[8px]" key={`name-single-${fieldKey}`}>
            {renderNameField(fieldKey, item.cardSectionTitle || (fieldKey === "firstName" ? "First name" : "Last name"))}
          </div>
        );
      }

      // --- Email with blur check ---
      case "email":
        return (
          <div className="flex flex-col gap-[4px]" key="email">
            <label className="text-small font-semibold">
              {item.cardSectionTitle || "Email address"}
              <span className="text-negative-default">*</span>
            </label>
            <input
              type="text"
              className={`w-full small font-normal px-[12px] py-[10px] border rounded-[4px] outline-none shadow-custom-2 ${errors.email ? `border-negative-default ${(emailShakeTick % 2 === 0) ? "field-invalid-shake-a" : "field-invalid-shake-b"}` : "border-grey-500 focus:border-blue-400"}`}
              value={values.email}
              maxLength={EMAIL_MAX_LENGTH}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? "leadform-email-error" : undefined}
              onKeyDown={handleEmailKeyDown}
              onPaste={handleEmailPaste}
              onChange={(e) => {
                const sanitized = sanitizeEmailValue(e.target.value).slice(0, EMAIL_MAX_LENGTH);
                onChange("email", sanitized);
                clearEmailInvalidCharTimers();
                setIsEmailInvalidCharFading(false);
                clearError("email");
                setFormError("");
              }}
              onBlur={handleEmailBlur}
              placeholder="Enter your email address"
            />
            <div id="leadform-email-error" role="alert" className={`x-small min-h-[18px] ${errors.email ? `text-negative-default ${errors.email === INVALID_CHAR_ERROR_MESSAGE || errors.email === EMAIL_LIMIT_REACHED_MESSAGE ? (isEmailInvalidCharFading ? "transition-opacity duration-[1200ms] ease-out opacity-0" : "opacity-100") : "opacity-100"}` : "hidden"}`}>
              {errors.email || "\u00A0"}
            </div>
          </div>
        );

      // --- Password with strength indicators ---
      case "password":
        return (
          <div className="flex flex-col gap-[8px]" key="password">
            <div className="flex flex-col gap-[4px]">
              <label htmlFor="password" className="small font-semibold">
                {item.cardSectionTitle || "Password"}
                <span className="text-negative-default">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`input-textbox w-full pr-[40px] placeholder-grey500 ${errors.password ? "border-negative-default" : ""}`}
                  placeholder="Please enter your password"
                  maxLength={PASSWORD_MAX_LENGTH}
                  value={values.password}
                  onChange={(e) => {
                    if (e.target.value.length >= PASSWORD_MAX_LENGTH && values.password.length >= PASSWORD_MAX_LENGTH) {
                      setTransientError("password", PASSWORD_LIMIT_REACHED_MESSAGE);
                      return;
                    }
                    onChange("password", e.target.value);
                    clearInvalidCharAnimation("password");
                    clearError("password");
                  }}
                />
                <button
                  className="absolute inset-y-0 right-[12px] flex items-center text-gray-500"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Image
                    src={showPassword ? "/static/assets/icons/show_password_icon.svg" : "/static/assets/icons/hide_password.svg"}
                    alt="Toggle password" width={20} height={20}
                  />
                </button>
              </div>
              <p role="alert" className={`x-small min-h-[18px] ${
                errors.password
                  ? `text-negative-default ${TRANSIENT_MESSAGES.has(errors.password || "") ? (isInvalidCharFading.password ? "transition-opacity duration-[1200ms] ease-out opacity-0" : "opacity-100") : "opacity-100"}`
                  : "hidden"
              }`}>{errors.password || "\u00A0"}</p>
            </div>
            <div className="flex justify-center divide-x divide-grey-200 small [&>*]:px-[8px]">
              <div className={getPasswordHelperColorClass(passwordRuleState.hasUppercase)}>ABC</div>
              <div className={getPasswordHelperColorClass(passwordRuleState.hasLowercase)}>abc</div>
              <div className={getPasswordHelperColorClass(passwordRuleState.hasNumber)}>123</div>
              <div className={getPasswordHelperColorClass(passwordRuleState.hasSpecial)}>!@%</div>
              <div className={getPasswordHelperColorClass(passwordRuleState.hasMinLength)}>8 characters</div>
            </div>
          </div>
        );

      // --- Year of Entry ---
      case "yearOfEntry":
        return (
          <div className="form_radio flex flex-col gap-[4px]" key="yearOfEntry">
            <label className="check-label small font-semibold text-grey300">
              {item.cardSectionTitle || "When will you start university?"}{" "}
              <span className="text-negative-default">*</span>
            </label>
            <div className="form-radio-group flex flex-row gap-[6px] md:gap-[8px]">
              {yearOfEntryOptions.length === 0 ? (
                <>
                  <div className="skeleton skeleton-text-animated !h-[36px] !w-[70px] !rounded-[18px] !m-0"></div>
                  <div className="skeleton skeleton-text-animated !h-[36px] !w-[70px] !rounded-[18px] !m-0"></div>
                  <div className="skeleton skeleton-text-animated !h-[36px] !w-[70px] !rounded-[18px] !m-0"></div>
                  <div className="skeleton skeleton-text-animated !h-[36px] !w-[70px] !rounded-[18px] !m-0"></div>
                </>
              ) : (
                yearOfEntryOptions.map((option) => (
                  <div className="flex relative" key={option.id}>
                    <input checked={values.yearOfEntry === String(option.id)} type="radio" name="yoe"
                      className="form-check-input rounded-[4px] outline-none absolute opacity-0 pointer-events-none"
                      id={`yoe-${option.id}`}
                      onChange={() => { onChange("yearOfEntry", String(option.id)); clearError("yearOfEntry"); }}
                    />
                    <label htmlFor={`yoe-${option.id}`}
                      className="check-label small font-semibold text-primary-400 bg-white border border-primary-400 rounded-[18px] px-[16px] py-[8px]"
                    >{option.value}</label>
                  </div>
                ))
              )}
            </div>
            {errors.yearOfEntry && <p className="x-small text-negative-default">{errors.yearOfEntry}</p>}
          </div>
        );

      // --- Study Level (fetched from sections API) ---
      case "studyLevel":
        return (
          <div className="flex flex-col gap-[8px]" key="studyLevel">
            <div className="small font-semibold">
              {item.cardSectionTitle || "Which level do you want to study at?"}
              <span className="text-negative-default">*</span>
            </div>
            <div className="flex flex-row flex-wrap gap-[8px]">
              {studyLevelOptions.length === 0 ? (
                <>
                  <div className="skeleton skeleton-text-animated !h-[36px] !w-[110px] !rounded-[9999px] !m-0"></div>
                  <div className="skeleton skeleton-text-animated !h-[36px] !w-[130px] !rounded-[9999px] !m-0"></div>
                  <div className="skeleton skeleton-text-animated !h-[36px] !w-[100px] !rounded-[9999px] !m-0"></div>
                  <div className="skeleton skeleton-text-animated !h-[36px] !w-[120px] !rounded-[9999px] !m-0"></div>
                </>
              ) : (
                studyLevelOptions.map((option) => (
                <div className="form_radio flex relative" key={option.id}>
                  <input checked={values.studyLevel === String(option.id)} type="radio" name="study"
                    className="form-check-input rounded-[4px] outline-none absolute opacity-0 pointer-events-none"
                    id={`studyLevel-${option.id}`}
                    onChange={() => { onChange("studyLevel", String(option.id)); clearError("studyLevel"); }}
                  />
                  <label htmlFor={`studyLevel-${option.id}`}
                    className="btn btn-primary-outline p-[8px_16px]"
                  >{option.label}</label>
                </div>
              ))
              )}
            </div>
            {errors.studyLevel && <p className="x-small text-negative-default">{errors.studyLevel}</p>}
          </div>
        );

      // --- Postcode (request info — optional) ---
      case "postcode":
        return (
          <div className="form_group flex flex-col gap-[8px] md:flex-row md:gap-[24px]" key="postcode">
            <div className="flex flex-col gap-[4px] flex-1 min-w-0">
              <label htmlFor="postcode" className="small font-semibold text-grey300">
                {item.cardSectionTitle || "Postcode"}
              </label>
              <div className="relative w-full">
                <input type="text"
                  className={`w-full small font-normal px-[12px] py-[10px] pr-[40px] border border-grey-500 rounded-[4px] outline-none shadow-custom-2 placeholder-grey500 ${errors.postcode ? "border-negative-default" : ""}`}
                  id="postcode" placeholder="Enter postcode"
                  value={values.postcode}
                  onChange={(e) => { onChange("postcode", e.target.value); clearError("postcode"); }}
                />
              </div>
              {errors.postcode && <p className="x-small text-negative-default">{errors.postcode}</p>}
            </div>
            <div className="flex flex-col justify-start gap-[4px] shrink-0">
              <div className="postalcode hints md:mt-[32px]">
                <button type="button" className="tooltip group/item relative small font-semibold text-primary-400 underline cursor-pointer">
                  Why do we need your postcode?
                  <div className="tooltip-wrap flex-col w-[320px] px-[12px] py-[12px] bg-white text-grey300 border border-grey-200 rounded-[8px] shadow-custom-12 mt-[3px] absolute right-[-100px] md:right-[-15px] z-[1] gap-[4px] after:w-[10px] after:h-[10px] after:absolute after:top-[-6px] after:left-[60%] after:bg-white after:z-[0] after:border after:border-grey-200 after:border-b-0 after:border-r-0 after:translate-[-50%] after:rotate-45 hidden group-hover/item:flex group-focus/item:flex after:content-[''] whitespace-normal">
                    <span className="font-semibold tooltip-head">Why do we need your postcode?</span>
                    <p className="x-small">We use this information to help assess the reach of our products. This is completely optional.</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        );

      // --- Open Day Selector ---
      case "openDaySelector":
        return (
          <div className="bg-grey-50 border border-grey-200 rounded-[8px] p-[16px]" key="openDaySelector">
            <div className="flex flex-col gap-[16px]">
              <label className="small font-semibold">{item.cardSectionTitle || "Choose an open day"}</label>
              <div className="relative">
                <button type="button"
                  className="w-full flex items-center justify-between small font-normal px-[12px] py-[10px] bg-grey-100 border border-grey-500 rounded-[4px] text-left"
                  onClick={() => setOpenDayDropdownOpen(!openDayDropdownOpen)}
                  aria-expanded={openDayDropdownOpen} aria-haspopup="listbox"
                >
                  <span>{values.openDayEventId || "Select an open day"}</span>
                  <Image src="/static/assets/icons/arrow_down_black.svg" alt="dropdown arrow" width={20} height={20}
                    className={`transition-transform ${openDayDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
            </div>
          </div>
        );

      // --- Stay in the Know ---
      case "stayInKnow":
        return (
          <React.Fragment key="stayInKnow">
            <hr className="bg-grey-200" />
            <div className="space-y-[8px]">
              <label className="small font-semibold">{item.cardSectionTitle || "Stay in the know"}</label>
              <CustomCheckbox checked={values.stayInKnow} onChange={(val) => onChange("stayInKnow", val)}>{item.longDescription || "We'll send helpful updates and occasionally invite you to share your views."}</CustomCheckbox>
            </div>
          </React.Fragment>
        );

      // --- Uni Newsletter (rendered separately below CMS fields) ---
      case "uniNewsletter":
        return null;

      // --- Country of Residence ---
      case "countryOfResidence":
        return (
          <div className="form_radio flex flex-col gap-[4px]" key="countryOfResidence">
            <label className="small font-semibold">{item.cardSectionTitle || "Country of residence"}<span className="text-negative-default">*</span></label>
            <div className="form-radio-group flex flex-row gap-[8px]">
              {["United Kingdom", "Rest of the world"].map((country) => (
                <div className="flex relative" key={country}>
                  <input checked={values.countryOfResidence === country} type="radio" name="countryOfResidence"
                    className="form-check-input rounded-[4px] outline-none absolute opacity-0 pointer-events-none"
                    id={`country-${country}`}
                    onChange={() => {
                      onChange("countryOfResidence", country);
                      clearError("countryOfResidence");
                      setAddressFound(false); setShowManualFields(false); setSelectedAddress(""); setAddressDropdownOpen(false);
                      onChange("addressLine1", ""); onChange("addressLine2", ""); onChange("townCity", ""); onChange("addressPostcode", ""); onChange("countrySelector", "");
                    }}
                  />
                  <label htmlFor={`country-${country}`}
                    className={`check-label small font-semibold rounded-[18px] px-[16px] py-[7px] cursor-pointer ${
                      values.countryOfResidence === country ? "bg-primary-500 text-white border border-primary-500" : "text-primary-400 bg-white border border-primary-400"
                    }`}
                  >{country}</label>
                </div>
              ))}
            </div>
            {errors.countryOfResidence && <p className="x-small text-negative-default">{errors.countryOfResidence}</p>}
          </div>
        );

      // --- Address Block (complete self-contained section) ---
      case "addressBlock": {
        const prevItems = cmsItems.slice(0, index);
        if (prevItems.some((prev) => getLeadFormFieldKey(prev) === "addressBlock")) return null;
        if (!hasCountryField) return <div className="flex flex-col gap-[24px]" key="addressBlock">{renderAddressFields()}</div>;
        if (!isUK && !isROW) return null;

        const handleFindAddress = async () => {
          const postcode = values.addressPostcode.trim();
          if (!postcode) {
            setErrors((prev) => ({ ...prev, addressBlock: "Please enter a valid postcode" }));
            return;
          }
          clearError("addressBlock");
          setIsSearchingAddress(true);
          try {
            const token = process.env.NEXT_PUBLIC_MAP_BOX_TOKEN || "";
            const res = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(postcode)}.json?country=gb&types=address,postcode&limit=10&access_token=${token}`,
            );
            if (!res.ok) {
              setErrors((prev) => ({ ...prev, addressBlock: "Unable to find addresses. Please try again." }));
              setIsSearchingAddress(false);
              return;
            }
            const data = await res.json();
            const features = data?.features || [];
            if (features.length === 0) {
              setErrors((prev) => ({ ...prev, addressBlock: "No addresses found for this postcode" }));
              setIsSearchingAddress(false);
              return;
            }
            const parsed = features.map((f: { place_name: string; text: string; context?: { id: string; text: string }[] }) => {
              const ctx = f.context || [];
              const getCtx = (prefix: string) => ctx.find((c: { id: string }) => c.id.startsWith(prefix))?.text || "";
              return {
                placeName: f.place_name,
                addressLine1: f.text || "",
                addressLine2: getCtx("locality") || getCtx("neighborhood"),
                city: getCtx("place"),
                postcode: getCtx("postcode") || postcode,
              };
            });
            setAddressSuggestions(parsed);
            setAddressFound(true);
            setAddressDropdownOpen(true);
          } catch {
            setErrors((prev) => ({ ...prev, addressBlock: "Unable to find addresses. Please try again." }));
          } finally {
            setIsSearchingAddress(false);
          }
        };
        const handleEnterManually = () => { setAddressFound(false); setShowManualFields(true); setAddressSuggestions([]); };
        const handleEditAddress = () => { setAddressFound(false); setShowManualFields(false); setAddressSuggestions([]); };

        return (
          <div className="flex flex-col gap-[24px]" key="addressBlock">
            {isROW && (
              <div className="flex flex-col gap-[4px]">
                <label className="small font-semibold">Country<span className="text-negative-default">*</span></label>
                <div className="relative">
                  <button type="button"
                    className="w-full flex items-center justify-between small font-normal px-[12px] py-[10px] bg-grey-100 border border-grey-500 rounded-[4px] text-left shadow-custom-2"
                    onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                    aria-expanded={countryDropdownOpen} aria-haspopup="listbox"
                  >
                    <span className="text-grey300">{values.countrySelector || "Choose country"}</span>
                    <Image src="/static/assets/icons/arrow_down_black.svg" alt="dropdown arrow" width={20} height={20}
                      className={`transition-transform ${countryDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {countryDropdownOpen && (
                    <div className="flex flex-col w-full absolute z-[1] bg-white shadow-custom-3 rounded-[8px] left-0 top-[52px]" role="listbox">
                      <div className="px-[16px] pt-[12px] pb-[8px]">
                        <p className="x-small font-semibold text-black tracking-[1px] uppercase">Choose Country</p>
                        {values.countrySelector && (
                          <p className="small font-semibold text-primary-400">
                            {values.countrySelector.toUpperCase()}
                          </p>
                        )}
                      </div>
                      <div className="px-[16px] pb-[8px]">
                        <span className="inline-block x-small font-semibold uppercase text-grey-500 bg-grey-100 rounded-[4px] px-[8px] py-[2px]">
                          Country
                        </span>
                      </div>
                      <div className="max-h-[200px] overflow-y-scroll custom-scrollbar-2">
                      {countryList.length === 0 ? (
                        <div className="px-[16px] py-[12px] flex flex-col gap-[8px]">
                          <div className="skeleton skeleton-text-animated !h-[20px] !w-[80%] !m-0"></div>
                          <div className="skeleton skeleton-text-animated !h-[20px] !w-[60%] !m-0"></div>
                          <div className="skeleton skeleton-text-animated !h-[20px] !w-[70%] !m-0"></div>
                          <div className="skeleton skeleton-text-animated !h-[20px] !w-[50%] !m-0"></div>
                          <div className="skeleton skeleton-text-animated !h-[20px] !w-[75%] !m-0"></div>
                        </div>
                      ) : (
                      countryList.map((country) => (
                        <button key={country.id} type="button"
                          className="block w-full text-left small px-[16px] py-[12px] hover:bg-blue-50 underline"
                          onClick={() => { onChange("countrySelector", country.label); setCountryDropdownOpen(false); clearError("addressBlock"); }}
                          role="option" aria-selected={values.countrySelector === country.label}
                        >{country.label}</button>
                      ))
                      )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {isUK && (
              <div className="flex flex-col gap-[4px]">
                <div className={`flex flex-col md:flex-row justify-between w-full ${showManualFields ? "" : "md:w-[calc(100%-140px)]"}`}>
                  <label className="small font-semibold">Postcode finder<span className="text-negative-default">*</span></label>
                  {!addressFound && <button type="button" className="small font-normal text-primary-400 cursor-pointer text-left" onClick={handleEnterManually}>Enter manually</button>}
                </div>
                {!addressFound && !showManualFields && (
                  <div className="flex gap-[8px] items-start">
                    <div className="flex-1 md:w-[calc(100%-140px)]">
                      <div className="relative">
                        <input type="text" className={`input-textbox w-full pr-[40px] ${errors.addressBlock ? "border-negative-default" : ""}`}
                          placeholder="Enter your postcode" value={values.addressPostcode}
                          onChange={(e) => { onChange("addressPostcode", e.target.value); clearError("addressBlock"); }}
                        />
                        <button type="button" className="absolute inset-y-0 right-[12px] flex items-center cursor-pointer" aria-label="Search postcode" onClick={handleFindAddress}>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 17L12.3333 12.3333M13.8889 8.44444C13.8889 11.4513 11.4513 13.8889 8.44444 13.8889C5.43756 13.8889 3 11.4513 3 8.44444C3 5.43756 5.43756 3 8.44444 3C11.4513 3 13.8889 5.43756 13.8889 8.44444Z" stroke="#5C656E" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                      </div>
                      {errors.addressBlock && <p className="x-small text-negative-default mt-[4px]">{errors.addressBlock}</p>}
                    </div>
                    <button type="button" className="btn btn-medium bg-primary-500 text-white hover:bg-primary-400 whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed" onClick={handleFindAddress} disabled={isSearchingAddress}>
                      {isSearchingAddress ? (
                        <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.5 12C4.5 7.875 7.875 4.5 12 4.5C16.125 4.5 19.5 7.875 19.5 12H22C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22V19.5C7.875 19.5 4.5 16.125 4.5 12Z" fill="white" />
                        </svg>
                      ) : "Find address"}
                    </button>
                  </div>
                )}
                {addressFound && (
                  <div className="flex gap-[8px] items-start">
                    <div className="flex-1 relative md:w-[calc(100%-140px)]">
                      <button type="button" className="w-full flex items-center justify-between small font-normal px-[12px] py-[10px] bg-grey-100 border border-grey-500 rounded-[4px] text-left"
                        onClick={() => setAddressDropdownOpen(!addressDropdownOpen)} aria-expanded={addressDropdownOpen} aria-haspopup="listbox"
                      >
                        <span className="truncate">{selectedAddress || (addressSuggestions[0]?.placeName ?? "Select an address")}</span>
                        <Image src="/static/assets/icons/arrow_down_black.svg" alt="dropdown arrow" width={20} height={20} className={`transition-transform ${addressDropdownOpen ? "rotate-180" : ""}`} />
                      </button>
                      {addressDropdownOpen && (
                        <div className="flex flex-col w-full absolute z-[1] bg-white shadow-custom-3 rounded-[8px] left-0 top-[52px]">
                          <div className="px-[16px] pt-[12px] pb-[8px]">
                            <p className="x-small font-semibold text-black tracking-[1px] leading-[18px] uppercase">Postcode</p>
                            <p className="small font-semibold text-primary-400">&apos;{values.addressPostcode.toUpperCase()}&apos;</p>
                          </div>
                          <div className="max-h-[200px] overflow-y-auto custom-scrollbar-2">
                            {addressSuggestions.map((suggestion) => (
                              <button key={suggestion.placeName} type="button" className="block w-full text-left small px-[16px] py-[12px] hover:bg-blue-50 underline truncate"
                                onClick={() => { setSelectedAddress(suggestion.placeName); setAddressDropdownOpen(false); onChange("addressLine1", suggestion.addressLine1); onChange("addressLine2", suggestion.addressLine2); onChange("townCity", suggestion.city); onChange("addressPostcode", suggestion.postcode); setShowManualFields(true); }}
                                role="option" aria-selected={selectedAddress === suggestion.placeName}
                                title={suggestion.placeName}
                              >{suggestion.placeName}</button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <button type="button" className="btn btn-medium bg-primary-500 text-white hover:bg-primary-400 whitespace-nowrap" onClick={handleEditAddress}>Edit address</button>
                  </div>
                )}
              </div>
            )}
            {showAddressFields && renderAddressFields()}
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (cmsItems.length === 0) return <LeadFormFieldsSkeleton />;

  return (
    <form className="flex flex-col gap-[24px]" onSubmit={onFormSubmit} noValidate>
      <div className="flex flex-col gap-[24px]">
        {cmsItems.map((item, index) => renderDynamicField(item, index))}
     

      {/* Client consent checkboxes — show on last step only (step 2 for physical, step 1 for digital/single) */}
      {(currentStep >= totalSteps - 1) && (
        clientConsents && clientConsents.length > 0 ? (
          <div className="space-y-[8px]">
            {clientConsents.map((consent) => (
              <label key={consent.institutionId} className="flex items-start gap-[12px] cursor-pointer">
                <input
                  type="checkbox"
                  className="peer hidden"
                  checked={checkedConsents.has(consent.institutionId)}
                  onChange={(e) => {
                    setCheckedConsents((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(consent.institutionId);
                      else next.delete(consent.institutionId);
                      return next;
                    });
                  }}
                />
                <div className="w-[16px] h-[16px] mt-[1px] border border-grey-400 rounded flex items-center justify-center peer-checked:bg-blue-400 peer-checked:border-blue-400 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100 flex-shrink-0">
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <span className="x-small text-grey-600 rtf-innerstyle" dangerouslySetInnerHTML={{ __html: consent.consentText }} />
              </label>
            ))}
          </div>
        ) : submissionContext?.clientConsentFlag === "Y" && submissionContext?.consentText ? (
          <div className="space-y-[8px] mt-[-16px]">
            <label className="flex items-start gap-[12px] cursor-pointer">
              <input
                type="checkbox"
                className="peer hidden"
                checked={checkedConsents.has("single")}
                onChange={(e) => {
                  setCheckedConsents((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add("single");
                    else next.delete("single");
                    return next;
                  });
                }}
              />
              <div className="w-[16px] h-[16px] mt-[1px] border border-grey-400 rounded flex items-center justify-center peer-checked:bg-blue-400 peer-checked:border-blue-400 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100 flex-shrink-0">
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <span className="x-small text-grey-600 rtf-innerstyle" dangerouslySetInnerHTML={{ __html: submissionContext.consentText }} />
            </label>
          </div>
        ) : null
      )}
 </div>
      <hr className="bg-grey-200" />

      {!((pageType === "digitalprospectus" || pageType === "physicalprospectus") && currentStep === 0 && isMultiStep) && (
      <p className="text-x-small text-grey-600">
        By continuing, I confirm I&apos;m over 13 and agree to the{" "}
        <a href={isPGS() ? `${domain}postgraduatesearch.com/terms.html` : "https://www.whatuni.com/wu-cont/termsAndCondition.htm"} target="_blank" rel="noopener noreferrer" className="text-primary-400">terms and conditions</a>{" "}and{" "}
        <a href={isPGS() ? `${domain}postgraduatesearch.com/privacy.html` : "https://www.whatuni.com/wu-cont/privacy.htm"} target="_blank" rel="noopener noreferrer" className="text-primary-400">privacy notice</a>
        <span className="text-negative-default">*</span>
      </p>
      )}

      {formError && (
        <p className="x-small text-negative-default text-center" role="alert">{formError}</p>
      )}

      <button type="submit"
        className="btn btn-primary w-full flex items-center justify-center gap-[8px] disabled:opacity-70 disabled:cursor-not-allowed"
        disabled={isSubmitting || hasProfanity}
      >
        {isSubmitting ? (
          <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 12C4.5 7.875 7.875 4.5 12 4.5C16.125 4.5 19.5 7.875 19.5 12H22C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22V19.5C7.875 19.5 4.5 16.125 4.5 12Z" fill="white" />
          </svg>
        ) : (
          <>
            {submitLabel || (pageType === "opendays" ? "Continue booking open day" : pageType === "digitalprospectus" ? "Request prospectus" : pageType === "physicalprospectus" ? "Complete to order your prospectuses" : "Request info")}
            <Image src="/static/assets/icons/right_white_arrow.svg" width={17} height={14} alt="arrow icon" />
          </>
        )}
      </button>
    </form>
  );
};

export default LeadFormFeilds;
